<template>
  <v-container>
    <h1>Organizations / Products</h1>
    <hr />

    <ProductsTable
      :products="products"
      :organizations="organizations"
      :loading="loading"
      @updateDiscount="promptUpdateDiscount"
      @assignToOrganization="promptAssignToOrganization"
      @delete="promptDelete"
    />

    <v-dialog
      v-if="deleteIndex > -1" :value="true"
      max-width="fit-content"
      @click:outside="onDeleteCancel"
    >
      <v-card>
        <v-card-title class="headline">Delete {{ products[deleteIndex].title }} ?</v-card-title>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="default-btn text" @click="onDeleteCancel">
            Cancel
          </v-btn>
          <v-btn class="default-btn red" @click="onDeleteConfirm">
            <span>Delete</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DialogAssignToOrganization
      v-if="showAssignOrganizationDialog"
      :name="showAssignOrganizationDialog.name"
      :organizations="organizations"
      @cancel="showAssignOrganizationDialog = null"
      @submit="assignToOrganization"
    />

    <DialogUpdateDiscount
      v-if="showDiscountDialog"
      :existingDiscount="showDiscountDialog.discount"
      @cancel="showDiscountDialog = null"
      @submit="updateProductDiscount"
    />

  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

import DialogAssignToOrganization from './DialogAssignToOrganization'
import DialogUpdateDiscount from './DialogUpdateDiscount'
import ProductsTable from './ProductsTable'

export default {
  name: 'Products',
  components: {
    DialogAssignToOrganization,
    DialogUpdateDiscount,
    ProductsTable,
  },
  data: () => ({
    loading: false,
    products: [],
    organizations: [],
    deleteIndex: -1,
    showDiscountDialog: null,
    showAssignOrganizationDialog: null,
  }),
  computed: {
    ...mapGetters('feathers', ['apiOrganizations', 'apiProducts']),
  },

  created () {
    this.initialize()
  },

  methods: {
    async initialize () {
      this.loading = true

      try {
        this.products = (await this.apiProducts.find({
          query: {
            $sort: {
              createdAt: -1,
            },
            $include: {
              MatchCount: true,
            },
          },
        })).map(product => Object.assign(product, {
          organizationId: product.organizationId || '',
        }))

        this.organizations = await this.apiOrganizations.find()

        this.$store.dispatch('lenderStore/setLenders', this.products)
      } catch (error) {
        this.addAlert({
          type: 'error',
          message: `Error retrieving product data: ${error.message}`,
        })
      }

      this.loading = false
    },

    promptDelete (item) {
      this.deleteIndex = this.products.indexOf(item)
    },

    onDeleteCancel () {
      this.deleteIndex = -1
    },

    async onDeleteConfirm () {
      try {
        await this.apiProducts.remove(this.products[this.deleteIndex].id)
        this.products.splice(this.deleteIndex, 1)

        this.addAlert({ message: 'The product was successfully deleted' })
      } catch (error) {
        this.addAlert({
          type: 'error',
          message: `There was an error deleting the product: ${error.message}`,
        })
      }

      this.deleteIndex = -1
    },

    promptUpdateDiscount (item) {
      this.showDiscountDialog = {
        id: item.id,
        index: this.products.indexOf(item),
        discount: item.discount,
      }
    },

    async updateProductDiscount (discount) {
      try {
        await this.apiProducts.patch(this.showDiscountDialog.id, { discount })

        this.products[this.showDiscountDialog.index].discount = discount
        this.showDiscountDialog = null

        this.addSuccessAlert('The discount was successfully applied')
      } catch (error) {
        this.addErrorAlert(`There was an error setting the discount: ${error.message}`)
      }
    },

    promptAssignToOrganization (item) {
      this.showAssignOrganizationDialog = {
        id: item.id,
        name: item.internalName || item.title,
        index: this.products.indexOf(item),
      }
    },

    async assignToOrganization (organizationId) {
      const { id, index } = this.showAssignOrganizationDialog

      try {
        await this.apiProducts.patch(id, { organizationId })

        this.products[index].organizationId = organizationId
        this.showAssignOrganizationDialog = null

        this.addSuccessAlert('The product was assigned to the organization')
      } catch (error) {
        this.addErrorAlert(`There was an error assigning the product to the organization: ${error.message}`)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.products-table {
  width: 100%;
  max-width: 1300px;
}

.action-row {
  padding-bottom: 40px;
}

::v-deep {
  th {
    white-space: nowrap;
  }

  td {
    width: 200px;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .v-dialog {
    box-shadow: none;
    overflow: scroll;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .v-row-group__header td {
    padding: 0 8px;
    height: 35px;
    vertical-align: bottom;
  }
}
</style>
